import { Node } from "@xyflow/react";
import { Check } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";

import { getAllForms } from "@/api/form";
import { useInfiniteQuery } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import toast from "react-hot-toast";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Modal from "@/components/ui/modal";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
}

interface ContentProps {
  setData: React.Dispatch<React.SetStateAction<Record<string, any> | null>>;
}

enum Type {
  form = 1,
  dynamic = 2,
  condition = 3,
  startNode = 4,
  operation = 5
}

enum TypeIcons {
  form = "file-text",
  dynamic = "code-xml",
  condition = "git-branch",
  startNode = "play",
  operation = "cog"
}

const defaultNodeDataByType = {
  form: {
    icon: TypeIcons.form,
    type: Type.form
  },
  dynamic: {
    icon: TypeIcons.dynamic,
    type: Type.dynamic
  },
  condition: {
    icon: TypeIcons.condition,
    type: Type.condition
  },
  startNode: {
    icon: TypeIcons.startNode,
    type: Type.startNode
  },
  operation: {
    icon: TypeIcons.operation,
    type: Type.operation
  }
} as const;

const AddNodeModal: React.FC<ModalProps> = ({ isOpen, onClose, setNodes }) => {
  const [newNodeData, setNewNodeData] = useState<Record<string, any> | null>(
    null
  );
  const [nodeType, setNodeType] = useState<keyof typeof Type | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const nodeTypes = [
    { value: "form", label: "فرم" },
    { value: "dynamic", label: "داینامیک" },
    { value: "condition", label: "شرط" },
    { value: "startNode", label: "گره شروع" },
    { value: "operation", label: "عملیات" }
  ];

  const [search, setSearch] = useState("");

  const filteredNodeTypes = nodeTypes.filter((item) =>
    item.label.toLowerCase().includes(search.toLowerCase())
  );

  const content = useMemo(() => {
    switch (nodeType) {
      case "form":
        return <FormContent setData={setNewNodeData} />;

      case "dynamic":
        return <DynamicContent setData={setNewNodeData} />;
      case "condition":
        return <ConditionContent setData={setNewNodeData} />;
      case "startNode":
        return <StartNodeContent setData={setNewNodeData} />;
      case "operation":
        return <OperationContent setData={setNewNodeData} />;
      default:
        return <></>;
    }
  }, [nodeType]);

  const handleCreateNode = () => {
    if (nodeType) {
      if (newNodeData) {
        const resolvedNodeData = {
          ...newNodeData,
          ...defaultNodeDataByType[nodeType]
        };

        if (
          nodeType === "condition" &&
          "query" in resolvedNodeData &&
          !String(resolvedNodeData.query ?? "").trim()
        ) {
          toast.error("کوئری شرط را وارد کنید");
          return;
        }
        
        console.log(
          "Creating node with type:",
          resolvedNodeData.type,
          "nodeType:",
          nodeType,
          "newNodeData:",
          resolvedNodeData
        );

        const nodesToAdd: Node[] = [];

        // اگر newNodeData آرایه است (چند فرم انتخاب شده)
        if (Array.isArray(newNodeData)) {
          for (let i = 0; i < newNodeData.length; i++) {
            const formData = newNodeData[i];
            const newNode: Node = {
              id: nanoid(),
              type: "custom",
              data: {
                ...formData,
                // می‌توانید اطلاعات اضافی هم اضافه کنید
                order: i + 1,
                totalCount: newNodeData.length
              },
              position: {
                x: Math.random() * 500,
                y: Math.random() * 200 + i * 150 // هر نود کمی پایین‌تر
              }
            };
            nodesToAdd.push(newNode);
          }
        }
        // اگر تک فرم است
        else {
          for (let i = 0; i < quantity; i++) {
            const newNode: Node = {
              id: nanoid(),
              type: "custom",
              data: resolvedNodeData,
              position: {
                x: Math.random() * 500,
                y: Math.random() * 200 + i * 150
              }
            };
            nodesToAdd.push(newNode);
          }
        }

        setNodes((prev) => [...prev, ...nodesToAdd]);
        onClose();
        toast.success(`${nodesToAdd.length} نود با موفقیت اضافه شد`);
      } else {
        toast.error("یک نوع را انتخاب کنید");
      }
    } else {
      toast.error("اطلاعات را تکمیل کنید");
    }
  };
  useEffect(() => {
    setNodeType(null);
    setNewNodeData(null);
    setQuantity(1);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="ساخت رشته جدید"
      description=""
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm">نوع گره</label>
          <Select
            onValueChange={(val) => {
              setNodeType(val as keyof typeof Type);
            }}
            dir="rtl"
          >
            <SelectTrigger>
              <SelectValue placeholder="انتخاب کنید" />
            </SelectTrigger>

            <SelectContent>
              <div className="p-2">
                <input
                  type="text"
                  placeholder="جستجو..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-md border px-2 py-1 text-sm outline-none"
                />
              </div>

              {filteredNodeTypes.length > 0 ? (
                filteredNodeTypes.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))
              ) : (
                <div className="text-muted-foreground p-2 text-center text-sm">
                  موردی پیدا نشد
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-4 space-y-1">
          <label className="text-sm">تعداد گره ها</label>
          <Input
            type="number"
            min="1"
            max="20"
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.max(1, parseInt(e.target.value) || 1))
            }
            placeholder="1"
            dir="rtl"
          />
        </div>

        <div className="mt-5">{content}</div>

        <Button className="mt-5 w-full" onClick={handleCreateNode}>
          اضافه کردن
        </Button>
      </div>
    </Modal>
  );
};

const FormContent: React.FC<ContentProps> = ({ setData }) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { ref, inView } = useInView();

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1500);

    return () => clearTimeout(timer);
  }, [search]);

  const { isFetchingNextPage, hasNextPage, isLoading, data, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["forms", debouncedSearch],
      queryFn: async ({ pageParam }) => {
        return await getAllForms({
          page: pageParam,
          size: 10,
          search: debouncedSearch
        });
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if (lastPage?.data.length === 0) {
          return undefined;
        }
        return lastPageParam + 1;
      }
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  const toggleSelect = (formId: number) => {
    setSelected((prev) => {
      if (prev.includes(formId)) {
        return prev.filter((id) => id !== formId);
      } else {
        return [...prev, formId];
      }
    });
  };

  const handleSubmitForms = () => {
    const selectedForms = data?.pages
      .flatMap((page) => page?.data || [])
      .filter((form) => selected.includes(form.id))
      .map((form) => ({
        name: form.name,
        formId: form.id,
        type: 1,
        icon: TypeIcons.form
      }));

    if (selectedForms && selectedForms.length > 0) {
      setData(selectedForms);
    }
  };

  if (isLoading)
    return (
      <div className="flex h-32 w-full items-center justify-center">
        <HashLoader color="#0099A5" size={40} />
      </div>
    );

  return (
    <>
      {selected.length > 0 && (
        <div className="mb-2 rounded-md bg-primary/10 p-2 text-center text-sm text-primary">
          {selected.length} فرم انتخاب شده است
        </div>
      )}

      <Input
        placeholder="جستجوی فرم..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
        dir="rtl"
      />
      <ScrollArea
        className="h-64 w-full rounded-md border border-slate-300"
        dir="rtl"
      >
        {data?.pages.map((page, index) => (
          <Fragment key={index}>
            {page?.data?.map((form) => (
              <div
                key={form.id}
                className={cn(
                  "flex cursor-pointer items-center border-b border-b-slate-300 px-5 py-3 text-sm transition-colors hover:bg-primary/20",
                  selected.includes(form.id) && "bg-primary/20"
                )}
                onClick={() => toggleSelect(form.id)}
              >
                {form.name}

                {selected.includes(form.id) && (
                  <Check className="ml-auto" size={20} color="#0099A5" />
                )}
              </div>
            ))}
          </Fragment>
        ))}

        {!isLoading && data?.pages?.[0]?.data?.length === 0 && (
          <div className="py-5 text-center text-sm text-slate-500">
            موردی پیدا نشد
          </div>
        )}

        {hasNextPage && (
          <div
            ref={ref}
            className="flex w-full justify-center py-2 text-center"
          >
            {isFetchingNextPage ? (
              <HashLoader size={30} color="#0099A5" />
            ) : (
              "بیشتر"
            )}
          </div>
        )}
      </ScrollArea>

      {selected.length > 0 && handleSubmitForms()}

      <Button
        variant="outline"
        className={cn("mt-5 w-full", selected.length > 0 && "mt-2")}
        onClick={() => navigate("/dashboard/form")}
      >
        افزودن فرم جدید
      </Button>
    </>
  );
};

const DynamicContent: React.FC<ContentProps> = ({ setData }) => {
  useEffect(() => {
    setData({
      icon: TypeIcons.dynamic,
      type: 2
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <label>نام فایل</label>
        <Input
          placeholder="نام فایل"
          onChange={(e) => {
            setData((prev) => ({ ...prev, DllName: e.target.value }));
          }}
        />
      </div>
      <div className="space-y-1">
        <label>فایل dll</label>
        <Input
          type="file"
          accept=".dll"
          onChange={() => {
            // TODO: handle upload file
            // setData((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
      </div>
    </div>
  );
};

const ConditionContent: React.FC<ContentProps> = ({ setData }) => {
  useEffect(() => {
    setData({
      icon: TypeIcons.condition,
      type: 3,
      formId: undefined,
      query: ""
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <label>نام شرط</label>
        <Input
          placeholder="نام شرط"
          onChange={(e) => {
            setData((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
      </div>

      <div className="space-y-1">
        <label>کوئری شرط (SQL)</label>
        <Textarea
          placeholder={`مثال: SELECT CASE WHEN EXISTS(SELECT 1 FROM ... WHERE ... AND WorkflowUserId=@WorkflowUserId) THEN 1 ELSE 0 END`}
          dir="rtl"
          onChange={(e) => {
            setData((prev) => ({ ...prev, query: e.target.value }));
          }}
        />
        <div className="text-xs text-slate-500" dir="rtl">
          پارامترهای قابل استفاده: @WorkflowUserId ، @UserId ، @ActiveRoleId
        </div>
      </div>
    </div>
  );
};

const StartNodeContent: React.FC<ContentProps> = ({ setData }) => {
  useEffect(() => {
    setData({
      icon: TypeIcons.startNode,
      type: 4,
      formId: undefined,
      name: "شروع"
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <label>نام گره شروع</label>
        <Input
          placeholder="نام گره شروع"
          defaultValue="شروع"
          onChange={(e) => {
            setData((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
      </div>
      <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
        این گره به عنوان نقطه شروع جریان کار استفاده خواهد شد.
      </div>
    </div>
  );
};

const OperationContent: React.FC<ContentProps> = ({ setData }) => {
  // State برای نگهداری مقدار ورودی‌ها
  const [operationName, setOperationName] = useState("");
  const [operationDetails, setOperationDetails] = useState("");

  // مقدار اولیه برای setData در هنگام بارگذاری کامپوننت
  useEffect(() => {
    setData({
      icon: TypeIcons.operation, // فرض می‌کنیم TypeIcons.operation تعریف شده است
      type: 5, // این مقدار باید مشخص شود
      name: operationName, // مقدار اولیه نام عملیات
      // dllName: null, // اگر فیلد دیگری لازم است
      query: operationDetails // مقدار اولیه جزئیات عملیات
    });
  }, [setData, operationName, operationDetails]); // وابستگی‌ها را به‌روز کردیم

  // مدیریت تغییر نام عملیات
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOperationName(e.target.value);
    // به‌روزرسانی داده‌ها در والد
    setData((prev: any) => ({
      ...prev,
      name: e.target.value
    }));
  };

  // مدیریت تغییر جزئیات عملیات
  const handleDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOperationDetails(e.target.value);
    // به‌روزرسانی داده‌ها در والد
    setData((prev: any) => ({
      ...prev,
      query: e.target.value // فرض می‌کنیم کلید "query" در داده والد استفاده می‌شود
    }));
  };

  return (
    <div className="space-y-5">
      {/* فیلد نام عملیات */}
      <div className="space-y-1">
        <label htmlFor="operation-name">نام عملیات</label>
        <Input
          id="operation-name"
          placeholder="نام عملیات را وارد کنید"
          value={operationName}
          onChange={handleNameChange}
        />
      </div>

      {/* فیلد جزئیات عملیات (مشابه کوئری SQL) */}
      <div className="space-y-1">
        <label htmlFor="operation-details">جزئیات عملیات</label>
        <Textarea
          id="operation-details"
          placeholder="جزئیات یا دستورالعمل عملیات را وارد کنید"
          value={operationDetails}
          onChange={handleDetailsChange}
          className="min-h-[100px]" // تنظیم ارتفاع حداقل برای Textarea
        />
      </div>
    </div>
  );
};

// const OperationContent: React.FC<ContentProps> = ({ setData }) => {
//   const [selected, setSelected] = useState<number | null>(null);
//   const { ref, inView } = useInView();

//   const navigate = useNavigate();

//   // Initialize on mount only - don't include setData in dependencies
//   useEffect(() => {
//     setData({
//       icon: TypeIcons.operation,
//       type: 5,
//       formId: undefined,
//       name: undefined
//     });
//   }, []);  // Empty dependency array - run only once on mount

//   const { isFetchingNextPage, isLoading, data, fetchNextPage } =
//     useInfiniteQuery({
//       queryKey: ["forms"],
//       queryFn: async ({ pageParam }) => {
//         return await getAllForms({ page: pageParam, size: 10 });
//       },
//       initialPageParam: 1,
//       getNextPageParam: (lastPage, _allPages, lastPageParam) => {
//         if (lastPage?.data.length === 0) {
//           return undefined;
//         }
//         return lastPageParam + 1;
//       }
//     });

//   useEffect(() => {
//     if (inView) {
//       fetchNextPage();
//     }
//   }, [fetchNextPage, inView]);

//   if (isLoading)
//     return (
//       <div className="flex h-32 w-full items-center justify-center">
//         <HashLoader color="#0099A5" size={40} />
//       </div>
//     );

//   return (
//     <>
//       <ScrollArea
//         className="h-64 w-full rounded-md border border-slate-300"
//         dir="rtl"
//       >
//         {data?.pages.map((page, index) => (
//           <Fragment key={index + 1}>
//             {page?.data.map((form) => (
//               <div
//                 key={form.id}
//                 className={cn(
//                   "flex cursor-pointer items-center border-b border-b-slate-300 px-5 py-3 text-sm transition-colors hover:bg-primary/20",
//                   selected === form.id && "pointer-events-none bg-primary/20"
//                 )}
//                 onClick={() => {
//                   const operationData = {
//                     name: form.name,
//                     formId: form.id,
//                     type: 5,
//                     icon: TypeIcons.operation,
//                     dllName: null
//                   };
//                   console.log("Operation node data:", operationData);
//                   setData(operationData);
//                   setSelected(form.id);
//                 }}
//               >
//                 {form.name}
//                 {selected === form.id && (
//                   <Check className="ml-auto" size={20} color="#0099A5" />
//                 )}
//               </div>
//             ))}
//           </Fragment>
//         ))}
//         <div ref={ref}>
//           {isFetchingNextPage && <HashLoader color="#0099A5" size={40} />}
//         </div>
//       </ScrollArea>

//       <Button
//         className="mt-2 w-full gap-2"
//         onClick={() => navigate("/form")}
//       >
//         <Plus size={24} />
//         فرم جدید
//       </Button>
//     </>
//   );
// };

export default AddNodeModal;
